import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { unsafeAPI } from '../../hooks/api';
import { Dialog } from '../../components/Dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import { Switch } from '../../shadcn/Switch';
import { Button } from '../../shadcn/Button';
import Notice from '../../components/Notice';
import Spinner from '../../components/Spinner';
import { Plus, Trash2, Pencil, Server, Shield, Route, ArrowRightLeft, Settings, Database } from 'lucide-react';
import * as Tabs from '@radix-ui/react-tabs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProviderConfig {
	enabled: boolean;
}

interface Rule {
	id: string;
	device_types?: string[];
	manufacturers?: string[];
	default_provider: string;
	backup_1?: string | null;
	backup_2?: string | null;
}

interface MdmRoutingConfig {
	providers: Record<string, ProviderConfig>;
	rules: Rule[];
	defaults: {
		default_provider: string;
		backup_1: string | null;
		backup_2: string | null;
	};
	restrictions: {
		tac: Record<string, { allow: string[]; deny: string[] }>;
		imei: Record<string, { allow: string[]; deny: string[] }>;
	};
	force_routes: {
		tac: Record<string, string>;
		imei: Record<string, string>;
	};
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
	tonic: 'Trustonic',
	nuovo: 'NuovoPay',
	kandji: 'Kandji',
	absolute: 'Absolute',
	paytrigger: 'PayTrigger',
	knox_guard: 'Knox Guard',
	honor_guard: 'Honor Guard',
	datacultr: 'Datacultr'
};

const DEVICE_TYPE_OPTIONS = ['phone', 'tablet', 'laptop'];

const parseBulkIdentifiers = (rawValue: string) =>
	Array.from(
		new Set(
			rawValue
				.split(/[\n,;]+/)
				.map((value) => value.replace(/\D/g, ''))
				.filter(Boolean)
		)
	);

const API_URL = 'https://api.krece.app/mdm_routing_settings/';

const TAB_ITEMS = [
	{ value: 'providers', label: 'Providers', Icon: Server },
	{ value: 'defaults', label: 'Defaults', Icon: Settings },
	{ value: 'rules', label: 'Rules', Icon: ArrowRightLeft },
	{ value: 'force_routes', label: 'Force Routes', Icon: Route },
	{ value: 'restrictions', label: 'Restrictions', Icon: Shield },
	{ value: 'seed', label: 'Seed / Import', Icon: Database }
] as const;

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function MdmRouting() {
	const { userData, internalCountry } = useContext(GlobalContext);

	const isSeedUser = userData?.email === 'jonathan@krece.app';
	const country = internalCountry.get() || userData?.country || '';

	const [mdmRouting, setMdmRouting] = useState<MdmRoutingConfig | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [activeTab, setActiveTab] = useState('providers');

	// Fetch config for the current country from headers
	useEffect(() => {
		if (!country) {
			setMdmRouting(null);
			setLoading(false);
			return;
		}
		(async () => {
			setLoading(true);
			setError('');
			const res = await unsafeAPI('GET', `${API_URL}?country=${country}`, null, userData, internalCountry.get());
			if (res?.success && res.mdm_routing) {
				setMdmRouting(res.mdm_routing);
			} else {
				setMdmRouting(null);
				if (res?.status) setError(res.status);
			}
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [country]);

	const apiPost = async (body: Record<string, any>): Promise<{ success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }> => {
		const res = await unsafeAPI('POST', API_URL, body, userData, internalCountry.get());
		if (!res) return { success: false, status: 'Error de conexión' };
		return res;
	};

	const handleWriteResult = (res: { success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }) => {
		if (res.success && res.mdm_routing) {
			setMdmRouting(res.mdm_routing);
			return true;
		}
		return res.status || 'Error al actualizar';
	};

	const reloadConfig = async () => {
		if (!country) return;
		const res = await unsafeAPI('GET', `${API_URL}?country=${country}`, null, userData, internalCountry.get());
		if (res?.success && res.mdm_routing) setMdmRouting(res.mdm_routing);
	};

	if (loading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Spinner size={2} />
			</div>
		);
	}

	const visibleTabs = isSeedUser ? TAB_ITEMS : TAB_ITEMS.filter((t) => t.value !== 'seed');

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-[1.25rem]">
			<div className="space-y-1">
				<h2 className="text-2xl font-semibold text-neutral-100">MDM Routing</h2>
				<p className="text-sm text-neutral-400">
					Configuración de proveedores MDM &mdash;{' '}
					<span className="font-medium text-neutral-200">{country === 'rd' ? 'RD' : country.charAt(0).toUpperCase() + country.slice(1)}</span>
				</p>
			</div>

			{error && <Notice message={error} variant="error" />}

			{!mdmRouting && !error && !loading && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-neutral-400">
					No hay configuración MDM para este país.
					{isSeedUser && (
						<span className="ml-2 cursor-pointer text-primary-400 hover:underline" onClick={() => setActiveTab('seed')}>
							Seed Country
						</span>
					)}
				</div>
			)}

			{/* Seed panel when no config exists (seed user only) */}
			{isSeedUser && !mdmRouting && !loading && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<SeedPanel
						country={country}
						onUpdate={apiPost}
						onResult={handleWriteResult}
						onConfigSeeded={reloadConfig}
						onCountryDeleted={() => setMdmRouting(null)}
					/>
				</div>
			)}

			{mdmRouting && (
				<Tabs.Root value={activeTab} onValueChange={setActiveTab}>
					<Tabs.List className="flex gap-1 overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-900/50 p-1">
						{visibleTabs.map((tab) => (
							<Tabs.Trigger
								key={tab.value}
								value={tab.value}
								className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-200 data-[state=active]:bg-neutral-800 data-[state=active]:text-neutral-100"
							>
								<tab.Icon className="size-4" />
								{tab.label}
							</Tabs.Trigger>
						))}
					</Tabs.List>

					<Tabs.Content value="providers" className="mt-4">
						<ProvidersPanel config={mdmRouting} country={country} onUpdate={apiPost} onResult={handleWriteResult} />
					</Tabs.Content>

					<Tabs.Content value="defaults" className="mt-4">
						<DefaultsPanel config={mdmRouting} country={country} onUpdate={apiPost} onResult={handleWriteResult} />
					</Tabs.Content>

					<Tabs.Content value="rules" className="mt-4">
						<RulesPanel config={mdmRouting} country={country} onUpdate={apiPost} onResult={handleWriteResult} />
					</Tabs.Content>

					<Tabs.Content value="force_routes" className="mt-4">
						<ForceRoutesPanel config={mdmRouting} country={country} onUpdate={apiPost} onResult={handleWriteResult} />
					</Tabs.Content>

					<Tabs.Content value="restrictions" className="mt-4">
						<RestrictionsPanel config={mdmRouting} country={country} onUpdate={apiPost} onResult={handleWriteResult} />
					</Tabs.Content>

					{isSeedUser && (
						<Tabs.Content value="seed" className="mt-4">
							<SeedPanel
								country={country}
								onUpdate={apiPost}
								onResult={handleWriteResult}
								onConfigSeeded={reloadConfig}
								onCountryDeleted={() => setMdmRouting(null)}
							/>
						</Tabs.Content>
					)}
				</Tabs.Root>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Shared panel props
// ---------------------------------------------------------------------------

interface PanelProps {
	config: MdmRoutingConfig;
	country: string;
	onUpdate: (body: Record<string, any>) => Promise<{ success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }>;
	onResult: (res: { success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }) => true | string;
}

// ---------------------------------------------------------------------------
// Providers Panel
// ---------------------------------------------------------------------------

function ProvidersPanel({ config, country, onUpdate, onResult }: PanelProps) {
	const [togglingProvider, setTogglingProvider] = useState<string | null>(null);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const providerOrder = Array.from(new Set([...Object.keys(config.providers), 'datacultr']));

	const handleToggle = async (provider: string, enabled: boolean) => {
		setTogglingProvider(provider);
		setNotice(null);
		const res = await onUpdate({ country, operation: 'toggle_provider', provider, enabled });
		const result = onResult(res);
		if (result === true) {
			setNotice({ message: `${PROVIDER_DISPLAY_NAMES[provider] || provider} ${enabled ? 'activado' : 'desactivado'}`, variant: 'success' });
		} else {
			setNotice({ message: result, variant: 'error' });
		}
		setTogglingProvider(null);
	};

	return (
		<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
			<h3 className="text-lg font-semibold text-neutral-100">Proveedores</h3>
			{notice && <Notice message={notice.message} variant={notice.variant} />}
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{providerOrder.map((key) => {
					const val = config.providers[key] || { enabled: false };
					return (
						<div key={key} className="flex items-center justify-between rounded-lg border border-neutral-800 bg-neutral-950/50 px-4 py-3">
							<span className="text-sm font-medium text-neutral-200">{PROVIDER_DISPLAY_NAMES[key] || key}</span>
							<Switch
								checked={val.enabled}
								disabled={togglingProvider === key}
								onCheckedChange={(checked) => handleToggle(key, checked)}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Defaults Panel
// ---------------------------------------------------------------------------

function DefaultsPanel({ config, country, onUpdate, onResult }: PanelProps) {
	const providerKeys = Object.keys(config.providers);
	const providerOptions = providerKeys.map((k) => ({ label: PROVIDER_DISPLAY_NAMES[k] || k, value: k }));
	const optionalProviderOptions = [{ label: 'Ninguno', value: '__none__' }, ...providerOptions];

	const [defaultProvider, setDefaultProvider] = useState(config.defaults.default_provider);
	const [backup1, setBackup1] = useState(config.defaults.backup_1 || '__none__');
	const [backup2, setBackup2] = useState(config.defaults.backup_2 || '__none__');
	const [saving, setSaving] = useState(false);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	useEffect(() => {
		setDefaultProvider(config.defaults.default_provider);
		setBackup1(config.defaults.backup_1 || '__none__');
		setBackup2(config.defaults.backup_2 || '__none__');
	}, [config.defaults]);

	const handleSave = async () => {
		setSaving(true);
		setNotice(null);
		const body: Record<string, any> = {
			country,
			operation: 'update_defaults',
			default_provider: defaultProvider
		};
		body.backup_1 = backup1 === '__none__' ? null : backup1;
		body.backup_2 = backup2 === '__none__' ? null : backup2;

		const res = await onUpdate(body);
		const result = onResult(res);
		if (result === true) {
			setNotice({ message: 'Defaults actualizados', variant: 'success' });
		} else {
			setNotice({ message: result, variant: 'error' });
		}
		setSaving(false);
	};

	return (
		<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
			<h3 className="text-lg font-semibold text-neutral-100">Defaults</h3>
			{notice && <Notice message={notice.message} variant={notice.variant} />}
			<div className="grid gap-4 sm:grid-cols-3">
				<Select
					floatingLabel="Default Provider"
					options={providerOptions}
					defaultValue={defaultProvider}
					onSelectedChange={(v) => v && setDefaultProvider(v as string)}
					/>
				<Select
					floatingLabel="Backup 1"
					options={optionalProviderOptions}
					defaultValue={backup1}
					onSelectedChange={(v) => setBackup1((v as string) || '__none__')}
				/>
				<Select
					floatingLabel="Backup 2"
					options={optionalProviderOptions}
					defaultValue={backup2}
					onSelectedChange={(v) => setBackup2((v as string) || '__none__')}
				/>
			</div>
			<div className="flex justify-end">
				<Button variant="primary" className="!w-fit" onClick={handleSave} loading={saving}>
					Guardar
				</Button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Rules Panel
// ---------------------------------------------------------------------------

function RulesPanel({ config, country, onUpdate, onResult }: PanelProps) {
	const [showDialog, setShowDialog] = useState(false);
	const [editingRule, setEditingRule] = useState<Rule | null>(null);
	const [deleteRule, setDeleteRule] = useState<Rule | null>(null);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	return (
		<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
			<div className="flex items-center justify-between">
				<h3 className="text-lg font-semibold text-neutral-100">Reglas de enrutamiento</h3>
				<Button
					variant="primary"
					className="!w-fit"
					Icon={<Plus className="size-4" />}
					onClick={() => {
						setEditingRule(null);
						setShowDialog(true);
					}}
				>
					Agregar Regla
				</Button>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			{config.rules.length === 0 ? (
				<p className="text-sm text-neutral-400">No hay reglas configuradas.</p>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-neutral-800 text-neutral-400">
								<th className="px-3 py-2 font-medium">ID</th>
								<th className="px-3 py-2 font-medium">Device Types</th>
								<th className="px-3 py-2 font-medium">Manufacturers</th>
								<th className="px-3 py-2 font-medium">Default</th>
								<th className="px-3 py-2 font-medium">Backup 1</th>
								<th className="px-3 py-2 font-medium">Backup 2</th>
								<th className="px-3 py-2 font-medium">Acciones</th>
							</tr>
						</thead>
						<tbody>
							{config.rules.map((rule) => (
								<tr key={rule.id} className="border-b border-neutral-800/50 text-neutral-200">
									<td className="px-3 py-2.5 font-mono text-xs">{rule.id}</td>
									<td className="px-3 py-2.5">
										<div className="flex flex-wrap gap-1">
											{(rule.device_types || []).map((dt) => (
												<Chip key={dt} label={dt} />
											))}
										</div>
									</td>
									<td className="px-3 py-2.5">
										<div className="flex flex-wrap gap-1">
											{(rule.manufacturers || []).map((m) => (
												<Chip key={m} label={m} />
											))}
										</div>
									</td>
									<td className="px-3 py-2.5">{PROVIDER_DISPLAY_NAMES[rule.default_provider] || rule.default_provider}</td>
									<td className="px-3 py-2.5 text-neutral-400">{rule.backup_1 ? PROVIDER_DISPLAY_NAMES[rule.backup_1] || rule.backup_1 : '-'}</td>
									<td className="px-3 py-2.5 text-neutral-400">{rule.backup_2 ? PROVIDER_DISPLAY_NAMES[rule.backup_2] || rule.backup_2 : '-'}</td>
									<td className="px-3 py-2.5">
										<div className="flex gap-1">
											<button
												className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-100"
												onClick={() => {
													setEditingRule(rule);
													setShowDialog(true);
												}}
											>
												<Pencil className="size-4" />
											</button>
											<button
												className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-red-900/30 hover:text-red-400"
												onClick={() => setDeleteRule(rule)}
											>
												<Trash2 className="size-4" />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{showDialog && (
				<RuleDialog
					rule={editingRule}
					providerKeys={Object.keys(config.providers)}
					onClose={() => {
						setShowDialog(false);
						setEditingRule(null);
					}}
					onSave={async (body) => {
						const res = await onUpdate({ country, ...body });
						const result = onResult(res);
						if (result === true) {
							setNotice({ message: editingRule ? 'Regla actualizada' : 'Regla creada', variant: 'success' });
							setShowDialog(false);
							setEditingRule(null);
							return true;
						}
						return result;
					}}
				/>
			)}

			{deleteRule && (
				<ConfirmDialog
					title="Eliminar regla"
					message={`¿Estás seguro de que deseas eliminar la regla "${deleteRule.id}"?`}
					onClose={() => setDeleteRule(null)}
					onConfirm={async () => {
						const res = await onUpdate({ country, operation: 'delete_rule', rule_id: deleteRule.id });
						const result = onResult(res);
						if (result === true) {
							setNotice({ message: 'Regla eliminada', variant: 'success' });
							setDeleteRule(null);
							return true;
						}
						return result;
					}}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Rule Dialog (Add / Edit)
// ---------------------------------------------------------------------------

function RuleDialog({
	rule,
	providerKeys,
	onClose,
	onSave
}: {
	rule: Rule | null;
	providerKeys: string[];
	onClose: () => void;
	onSave: (body: Record<string, any>) => Promise<true | string>;
}) {
	const providerOptions = providerKeys.map((k) => ({ label: PROVIDER_DISPLAY_NAMES[k] || k, value: k }));
	const optionalProviderOptions = [{ label: 'Ninguno', value: '__none__' }, ...providerOptions];

	const [ruleId, setRuleId] = useState(rule?.id || '');
	const [deviceTypes, setDeviceTypes] = useState<string[]>(rule?.device_types || []);
	const [manufacturers, setManufacturers] = useState(rule?.manufacturers?.join(', ') || '');
	const [defaultProvider, setDefaultProvider] = useState(rule?.default_provider || providerKeys[0] || '');
	const [backup1, setBackup1] = useState(rule?.backup_1 || '__none__');
	const [backup2, setBackup2] = useState(rule?.backup_2 || '__none__');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const handleSubmit = async () => {
		if (!ruleId.trim()) {
			setError('Rule ID es requerido');
			return;
		}
		if (!defaultProvider) {
			setError('Default Provider es requerido');
			return;
		}

		setSaving(true);
		setError('');

		const parsedManufacturers = manufacturers
			.split(',')
			.map((m) => m.trim().toLowerCase())
			.filter(Boolean);

		const body: Record<string, any> = {
			operation: rule ? 'update_rule' : 'set_rule_chain',
			rule_id: ruleId.trim(),
			default_provider: defaultProvider,
			device_types: deviceTypes,
			manufacturers: parsedManufacturers,
			backup_1: backup1 === '__none__' ? null : backup1,
			backup_2: backup2 === '__none__' ? null : backup2
		};

		const result = await onSave(body);
		if (result !== true) setError(result);
		setSaving(false);
	};

	const toggleDeviceType = (dt: string) => {
		setDeviceTypes((prev) => (prev.includes(dt) ? prev.filter((d) => d !== dt) : [...prev, dt]));
	};

	return (
		<Dialog title={rule ? 'Editar Regla' : 'Agregar Regla'} onClose={onClose} cerrarButton={false}>
			<form
				className="flex flex-col gap-4"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<Input
					floatingLabel="Rule ID"
					placeholder="e.g. samsung, transsion_family"
					value={ruleId}
					onChange={setRuleId}
					disabled={!!rule}
					clearable={false}
				/>

				<div className="space-y-1">
					<label className="text-sm font-medium text-neutral-300">Device Types</label>
					<div className="flex flex-wrap gap-2">
						{DEVICE_TYPE_OPTIONS.map((dt) => (
							<button
								key={dt}
								type="button"
								onClick={() => toggleDeviceType(dt)}
								className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
									deviceTypes.includes(dt)
										? 'border-primary-500 bg-primary-500/20 text-primary-300'
										: 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
								}`}
							>
								{dt}
							</button>
						))}
					</div>
				</div>

				<Input
					floatingLabel="Manufacturers"
					placeholder="tecno, infinix, itel (separados por coma)"
					value={manufacturers}
					onChange={setManufacturers}
					clearable={false}
				/>

				<Select
					floatingLabel="Default Provider"
					options={providerOptions}
					defaultValue={defaultProvider}
					onSelectedChange={(v) => v && setDefaultProvider(v as string)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<Select
						floatingLabel="Backup 1"
						options={optionalProviderOptions}
						defaultValue={backup1}
						onSelectedChange={(v) => setBackup1((v as string) || '__none__')}
					/>
					<Select
						floatingLabel="Backup 2"
						options={optionalProviderOptions}
						defaultValue={backup2}
						onSelectedChange={(v) => setBackup2((v as string) || '__none__')}
					/>
				</div>

				{error && <Notice message={error} variant="error" />}

				<div className="flex justify-end gap-3">
					<Button variant="secondary" className="!w-fit" onClick={onClose}>
						Cancelar
					</Button>
					<Button type="submit" variant="primary" className="!w-fit" loading={saving}>
						{rule ? 'Actualizar' : 'Crear'}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Force Routes Panel
// ---------------------------------------------------------------------------

function ForceRoutesPanel({ config, country, onUpdate, onResult }: PanelProps) {
	const [subTab, setSubTab] = useState('tac');
	const [showDialog, setShowDialog] = useState(false);
	const [deleteEntry, setDeleteEntry] = useState<{ type: string; value: string } | null>(null);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	const forceRoutes = subTab === 'tac' ? config.force_routes.tac : config.force_routes.imei;
	const entries = Object.entries(forceRoutes || {});

	return (
		<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
			<div className="flex items-center justify-between">
				<h3 className="text-lg font-semibold text-neutral-100">Force Routes</h3>
				<Button variant="primary" className="!w-fit" Icon={<Plus className="size-4" />} onClick={() => setShowDialog(true)}>
					Agregar
				</Button>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			<Tabs.Root value={subTab} onValueChange={setSubTab}>
				<Tabs.List className="flex gap-1 rounded-md border border-neutral-800 p-1">
					<Tabs.Trigger
						value="tac"
						className="rounded px-3 py-1.5 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-200 data-[state=active]:bg-neutral-800 data-[state=active]:text-neutral-100"
					>
						TAC
					</Tabs.Trigger>
					<Tabs.Trigger
						value="imei"
						className="rounded px-3 py-1.5 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-200 data-[state=active]:bg-neutral-800 data-[state=active]:text-neutral-100"
					>
						IMEI
					</Tabs.Trigger>
				</Tabs.List>
			</Tabs.Root>

			{entries.length === 0 ? (
				<p className="text-sm text-neutral-400">No hay force routes de tipo {subTab.toUpperCase()}.</p>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-neutral-800 text-neutral-400">
								<th className="px-3 py-2 font-medium">Identificador ({subTab.toUpperCase()})</th>
								<th className="px-3 py-2 font-medium">Provider</th>
								<th className="px-3 py-2 font-medium">Acciones</th>
							</tr>
						</thead>
						<tbody>
							{entries.map(([value, provider]) => (
								<tr key={value} className="border-b border-neutral-800/50 text-neutral-200">
									<td className="px-3 py-2.5 font-mono text-xs">{value}</td>
									<td className="px-3 py-2.5">{PROVIDER_DISPLAY_NAMES[provider] || provider}</td>
									<td className="px-3 py-2.5">
										<button
											className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-red-900/30 hover:text-red-400"
											onClick={() => setDeleteEntry({ type: subTab, value })}
										>
											<Trash2 className="size-4" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{showDialog && (
				<ForceRouteDialog
					defaultType={subTab}
					providerKeys={Object.keys(config.providers)}
					onClose={() => setShowDialog(false)}
					onSave={async (body) => {
						const { values: rawValues, ...singleBody } = body;
						const values = Array.isArray(rawValues) ? rawValues : body.value ? [body.value] : [];

						for (const value of values) {
							const res = await onUpdate({ country, ...singleBody, value });
							const result = onResult(res);
							if (result !== true) return `${value}: ${result}`;
						}

						setNotice({ message: values.length > 1 ? `${values.length} force routes agregados` : 'Force route agregado', variant: 'success' });
						setShowDialog(false);
						return true;
					}}
				/>
			)}

			{deleteEntry && (
				<ConfirmDialog
					title="Eliminar force route"
					message={`¿Eliminar force route ${deleteEntry.type.toUpperCase()} "${deleteEntry.value}"?`}
					onClose={() => setDeleteEntry(null)}
					onConfirm={async () => {
						const res = await onUpdate({
							country,
							operation: 'remove_force_route',
							identifier_type: deleteEntry.type,
							value: deleteEntry.value
						});
						const result = onResult(res);
						if (result === true) {
							setNotice({ message: 'Force route eliminado', variant: 'success' });
							setDeleteEntry(null);
							return true;
						}
						return result;
					}}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Force Route Dialog
// ---------------------------------------------------------------------------

function ForceRouteDialog({
	defaultType,
	providerKeys,
	onClose,
	onSave
}: {
	defaultType: string;
	providerKeys: string[];
	onClose: () => void;
	onSave: (body: Record<string, any>) => Promise<true | string>;
}) {
	const providerOptions = providerKeys.map((k) => ({ label: PROVIDER_DISPLAY_NAMES[k] || k, value: k }));

	const [identifierType, setIdentifierType] = useState(defaultType);
	const [value, setValue] = useState('');
	const [provider, setProvider] = useState(providerKeys[0] || '');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const expectedLength = identifierType === 'tac' ? 8 : 15;

	const parsedValues = parseBulkIdentifiers(value);

	const handleSubmit = async () => {
		if (!parsedValues.length) {
			setError(`Ingresa al menos un ${identifierType.toUpperCase()}`);
			return;
		}

		const invalidValues = parsedValues.filter((digits) => digits.length !== expectedLength);
		if (invalidValues.length) {
			setError(`${identifierType.toUpperCase()} debe tener ${expectedLength} dígitos: ${invalidValues.join(', ')}`);
			return;
		}
		if (!provider) {
			setError('Selecciona un provider');
			return;
		}

		setSaving(true);
		setError('');
		const result = await onSave({ operation: 'force_route', identifier_type: identifierType, values: parsedValues, provider });
		if (result !== true) setError(result);
		setSaving(false);
	};

	return (
		<Dialog title="Agregar Force Route" onClose={onClose} cerrarButton={false}>
			<form
				className="flex flex-col gap-4"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<div className="space-y-1">
					<label className="text-sm font-medium text-neutral-300">Tipo de identificador</label>
					<div className="flex gap-2">
						{['tac', 'imei'].map((t) => (
							<button
								key={t}
								type="button"
								onClick={() => {
									setIdentifierType(t);
									setValue('');
								}}
								className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
									identifierType === t
										? 'border-primary-500 bg-primary-500/20 text-primary-300'
										: 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
								}`}
							>
								{t.toUpperCase()}
							</button>
						))}
					</div>
				</div>

				<div className="space-y-2">
					<Textarea
						floatingLabel={`${identifierType.toUpperCase()} (${expectedLength} dígitos)`}
						placeholder={identifierType === 'tac' ? '35828812\n35828813' : '358288123456789\n358288123456780'}
						value={value}
						onChange={setValue}
						onSubmit={() => handleSubmit()}
						rows={4}
						clearable={false}
					/>
					<p className="text-xs text-neutral-500">
						Pegue múltiples {identifierType.toUpperCase()} separados por líneas, comas o punto y coma. Use Ctrl+Enter para agregar.
					</p>
				</div>

				<Select
					floatingLabel="Provider"
					options={providerOptions}
					defaultValue={provider}
					onSelectedChange={(v) => v && setProvider(v as string)}
				/>

				{error && <Notice message={error} variant="error" />}

				<div className="flex justify-end gap-3">
					<Button variant="secondary" className="!w-fit" onClick={onClose}>
						Cancelar
					</Button>
					<Button type="submit" variant="primary" className="!w-fit" loading={saving}>
						Agregar{parsedValues.length > 1 ? ` ${parsedValues.length}` : ''}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Restrictions Panel
// ---------------------------------------------------------------------------

function RestrictionsPanel({ config, country, onUpdate, onResult }: PanelProps) {
	const [subTab, setSubTab] = useState('tac');
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [showDialog, setShowDialog] = useState(false);
	const [deleteEntry, setDeleteEntry] = useState<{ provider: string; mode: 'allow' | 'deny'; value: string } | null>(null);

	const restrictions = subTab === 'tac' ? config.restrictions.tac : config.restrictions.imei;

	// Flatten provider entries into rows: { provider, mode, value }
	const rows: { provider: string; mode: 'allow' | 'deny'; value: string }[] = [];
	for (const [provider, lists] of Object.entries(restrictions || {})) {
		for (const val of lists.allow) rows.push({ provider, mode: 'allow', value: val });
		for (const val of lists.deny) rows.push({ provider, mode: 'deny', value: val });
	}

	return (
		<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
			<div className="flex items-center justify-between">
				<h3 className="text-lg font-semibold text-neutral-100">Restricciones</h3>
				<Button variant="primary" className="!w-fit" Icon={<Plus className="size-4" />} onClick={() => setShowDialog(true)}>
					Agregar
				</Button>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			<Tabs.Root value={subTab} onValueChange={setSubTab}>
				<Tabs.List className="flex gap-1 rounded-md border border-neutral-800 p-1">
					<Tabs.Trigger
						value="tac"
						className="rounded px-3 py-1.5 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-200 data-[state=active]:bg-neutral-800 data-[state=active]:text-neutral-100"
					>
						TAC
					</Tabs.Trigger>
					<Tabs.Trigger
						value="imei"
						className="rounded px-3 py-1.5 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-200 data-[state=active]:bg-neutral-800 data-[state=active]:text-neutral-100"
					>
						IMEI
					</Tabs.Trigger>
				</Tabs.List>
			</Tabs.Root>

			{rows.length === 0 ? (
				<p className="text-sm text-neutral-400">No hay restricciones de tipo {subTab.toUpperCase()}.</p>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-neutral-800 text-neutral-400">
								<th className="px-3 py-2 font-medium">Identificador ({subTab.toUpperCase()})</th>
								<th className="px-3 py-2 font-medium">Provider</th>
								<th className="px-3 py-2 font-medium">Modo</th>
								<th className="px-3 py-2 font-medium">Acciones</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr key={`${row.provider}-${row.mode}-${row.value}`} className="border-b border-neutral-800/50 text-neutral-200">
									<td className="px-3 py-2.5 font-mono text-xs">{row.value}</td>
									<td className="px-3 py-2.5">{PROVIDER_DISPLAY_NAMES[row.provider] || row.provider}</td>
									<td className="px-3 py-2.5">
										<span
											className={`rounded-md border px-2 py-0.5 text-xs ${
												row.mode === 'allow'
													? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
													: 'border-red-500/30 bg-red-500/10 text-red-400'
											}`}
										>
											{row.mode === 'allow' ? 'Allow' : 'Deny'}
										</span>
									</td>
									<td className="px-3 py-2.5">
										<button
											className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-red-900/30 hover:text-red-400"
											onClick={() => setDeleteEntry(row)}
										>
											<Trash2 className="size-4" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{showDialog && (
				<RestrictionDialog
					identifierType={subTab}
					defaultProvider=""
					providerKeys={Object.keys(config.providers)}
					onClose={() => setShowDialog(false)}
					onSave={async (body) => {
						const { values: rawValues, ...singleBody } = body;
						const values = Array.isArray(rawValues) ? rawValues : body.value ? [body.value] : [];

						for (const value of values) {
							const res = await onUpdate({ country, ...singleBody, value });
							const result = onResult(res);
							if (result !== true) return `${value}: ${result}`;
						}

						setNotice({ message: values.length > 1 ? `${values.length} restricciones agregadas` : 'Restricción agregada', variant: 'success' });
						setShowDialog(false);
						return true;
					}}
				/>
			)}

			{deleteEntry && (
				<ConfirmDialog
					title="Eliminar restricción"
					message={`¿Eliminar restricción ${subTab.toUpperCase()} "${deleteEntry.value}" (${deleteEntry.mode}) de ${PROVIDER_DISPLAY_NAMES[deleteEntry.provider] || deleteEntry.provider}?`}
					onClose={() => setDeleteEntry(null)}
					onConfirm={async () => {
						const res = await onUpdate({
							country,
							operation: 'allow_deny_identifier',
							identifier_type: subTab,
							value: deleteEntry.value,
							provider: deleteEntry.provider,
							mode: deleteEntry.mode,
							enabled: false
						});
						const result = onResult(res);
						if (result === true) {
							setNotice({ message: 'Restricción eliminada', variant: 'success' });
							setDeleteEntry(null);
							return true;
						}
						return result;
					}}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Restriction Dialog
// ---------------------------------------------------------------------------

function RestrictionDialog({
	identifierType,
	defaultProvider,
	providerKeys,
	onClose,
	onSave
}: {
	identifierType: string;
	defaultProvider: string;
	providerKeys: string[];
	onClose: () => void;
	onSave: (body: Record<string, any>) => Promise<true | string>;
}) {
	const providerOptions = providerKeys.map((k) => ({ label: PROVIDER_DISPLAY_NAMES[k] || k, value: k }));

	const [provider, setProvider] = useState(defaultProvider || providerKeys[0] || '');
	const [value, setValue] = useState('');
	const [mode, setMode] = useState<'allow' | 'deny'>('deny');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const expectedLength = identifierType === 'tac' ? 8 : 15;

	const parsedValues = parseBulkIdentifiers(value);

	const handleSubmit = async () => {
		if (!parsedValues.length) {
			setError(`Ingresa al menos un ${identifierType.toUpperCase()}`);
			return;
		}

		const invalidValues = parsedValues.filter((digits) => digits.length !== expectedLength);
		if (invalidValues.length) {
			setError(`${identifierType.toUpperCase()} debe tener ${expectedLength} dígitos: ${invalidValues.join(', ')}`);
			return;
		}
		if (!provider) {
			setError('Selecciona un provider');
			return;
		}

		setSaving(true);
		setError('');
		const result = await onSave({
			operation: 'allow_deny_identifier',
			identifier_type: identifierType,
			values: parsedValues,
			provider,
			mode,
			enabled: true
		});
		if (result !== true) setError(result);
		setSaving(false);
	};

	return (
		<Dialog title="Agregar restricción" onClose={onClose} cerrarButton={false}>
			<form
				className="flex flex-col gap-4"
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<Select
					floatingLabel="Provider"
					options={providerOptions}
					defaultValue={provider}
					onSelectedChange={(v) => v && setProvider(v as string)}
				/>

				<div className="space-y-2">
					<Textarea
						floatingLabel={`${identifierType.toUpperCase()} (${expectedLength} dígitos)`}
						placeholder={identifierType === 'tac' ? '35828812\n35828813' : '358288123456789\n358288123456780'}
						value={value}
						onChange={setValue}
						onSubmit={() => handleSubmit()}
						rows={4}
						clearable={false}
					/>
					<p className="text-xs text-neutral-500">
						Pegue múltiples {identifierType.toUpperCase()} separados por líneas, comas o punto y coma. Use Ctrl+Enter para agregar.
					</p>
				</div>

				<div className="space-y-1">
					<label className="text-sm font-medium text-neutral-300">Modo</label>
					<div className="flex gap-2">
						{(['allow', 'deny'] as const).map((m) => (
							<button
								key={m}
								type="button"
								onClick={() => setMode(m)}
								className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
									mode === m
										? m === 'allow'
											? 'border-emerald-500 bg-emerald-500/20 text-emerald-300'
											: 'border-red-500 bg-red-500/20 text-red-300'
										: 'border-neutral-700 text-neutral-400 hover:border-neutral-600'
								}`}
							>
								{m === 'allow' ? 'Allow' : 'Deny'}
							</button>
						))}
					</div>
				</div>

				{error && <Notice message={error} variant="error" />}

				<div className="flex justify-end gap-3">
					<Button variant="secondary" className="!w-fit" onClick={onClose}>
						Cancelar
					</Button>
					<Button type="submit" variant="primary" className="!w-fit" loading={saving}>
						Agregar{parsedValues.length > 1 ? ` ${parsedValues.length}` : ''}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Seed Panel
// ---------------------------------------------------------------------------

function SeedPanel({
	country,
	onUpdate,
	onResult,
	onConfigSeeded,
	onCountryDeleted
}: {
	country: string;
	onUpdate: (body: Record<string, any>) => Promise<{ success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }>;
	onResult: (res: { success: boolean; status?: string; mdm_routing?: MdmRoutingConfig }) => true | string;
	onConfigSeeded: () => Promise<void>;
	onCountryDeleted: () => void;
}) {
	const [seedCountry, setSeedCountry] = useState(country || '');
	const [seedJson, setSeedJson] = useState('');
	const [bulkJson, setBulkJson] = useState('');
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
	const [seedLoading, setSeedLoading] = useState(false);
	const [bulkLoading, setBulkLoading] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState(false);

	const handleSeedCountry = async () => {
		if (!seedCountry.trim()) {
			setNotice({ message: 'Ingresa un país', variant: 'error' });
			return;
		}
		let parsed;
		try {
			parsed = JSON.parse(seedJson);
		} catch {
			setNotice({ message: 'JSON inválido', variant: 'error' });
			return;
		}

		setSeedLoading(true);
		setNotice(null);
		const res = await onUpdate({ country: seedCountry.trim().toLowerCase(), operation: 'seed_country', mdm_routing: parsed });
		const result = onResult(res);
		if (result === true) {
			setNotice({ message: `País "${seedCountry}" inicializado`, variant: 'success' });
			await onConfigSeeded();
		} else {
			setNotice({ message: result, variant: 'error' });
		}
		setSeedLoading(false);
	};

	const handleSeedBulk = async () => {
		let parsed;
		try {
			parsed = JSON.parse(bulkJson);
		} catch {
			setNotice({ message: 'JSON inválido', variant: 'error' });
			return;
		}

		setBulkLoading(true);
		setNotice(null);
		const res = await onUpdate({ operation: 'seed_bulk', entries: parsed });
		if (res.success) {
			setNotice({ message: 'Seed masivo completado', variant: 'success' });
			await onConfigSeeded();
		} else {
			setNotice({ message: res.status || 'Error', variant: 'error' });
		}
		setBulkLoading(false);
	};

	return (
		<div className="space-y-6">
			<h3 className="text-lg font-semibold text-neutral-100">Seed / Import</h3>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			{/* Seed Country */}
			<div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-950/30 p-4">
				<h4 className="text-sm font-semibold text-neutral-200">Seed Country</h4>
				<Input
					floatingLabel="País"
					placeholder="e.g. peru"
					value={seedCountry}
					onChange={setSeedCountry}
					clearable={false}
				/>
				<div className="space-y-1">
					<label className="text-sm font-medium text-neutral-300">Config JSON</label>
					<textarea
						className="h-48 w-full rounded-lg border border-neutral-800 bg-neutral-925 p-3 font-mono text-sm text-neutral-200 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
						placeholder='{"providers": {...}, "rules": [], "defaults": {...}, "restrictions": {...}, "force_routes": {...}}'
						value={seedJson}
						onChange={(e) => setSeedJson(e.target.value)}
					/>
				</div>
				<Button variant="primary" className="!w-fit" onClick={handleSeedCountry} loading={seedLoading}>
					Seed Country
				</Button>
			</div>

			{/* Seed Bulk */}
			<div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-950/30 p-4">
				<h4 className="text-sm font-semibold text-neutral-200">Seed Bulk</h4>
				<div className="space-y-1">
					<label className="text-sm font-medium text-neutral-300">Entries JSON</label>
					<textarea
						className="h-48 w-full rounded-lg border border-neutral-800 bg-neutral-925 p-3 font-mono text-sm text-neutral-200 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
						placeholder='[{"country": "peru", "mdm_routing": {...}}, ...]'
						value={bulkJson}
						onChange={(e) => setBulkJson(e.target.value)}
					/>
				</div>
				<Button variant="primary" className="!w-fit" onClick={handleSeedBulk} loading={bulkLoading}>
					Seed All
				</Button>
			</div>

			{/* Delete Config */}
			{country && (
				<div className="space-y-3 rounded-lg border border-red-900/50 bg-red-950/10 p-4">
					<h4 className="text-sm font-semibold text-red-400">Zona de peligro</h4>
					<p className="text-sm text-neutral-400">
						Eliminar toda la configuración MDM de <strong className="text-neutral-200">{country}</strong>. Esta acción es irreversible.
					</p>
					<Button variant="destructive" className="!w-fit" onClick={() => setDeleteDialog(true)}>
						Eliminar configuración MDM
					</Button>
				</div>
			)}

			{deleteDialog && country && (
				<ConfirmDialog
					title="Eliminar configuración MDM"
					message={`¿Estás seguro de que deseas eliminar TODA la configuración MDM para "${country}"? Esta acción es irreversible.`}
					onClose={() => setDeleteDialog(false)}
					onConfirm={async () => {
						const res = await onUpdate({ country, operation: 'delete_config' });
						if (res.success) {
							setDeleteDialog(false);
							onCountryDeleted();
							return true;
						}
						return res.status || 'Error al eliminar';
					}}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Chip component
// ---------------------------------------------------------------------------

function Chip({ label, onRemove }: { label: string; onRemove?: () => void }) {
	return (
		<span className="inline-flex items-center gap-1 rounded-md bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-300">
			{label}
			{onRemove && (
				<button type="button" onClick={onRemove} className="ml-0.5 text-neutral-500 transition-colors hover:text-neutral-200">
					&times;
				</button>
			)}
		</span>
	);
}
